import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  LinearProgress,
  IconButton,
  Tabs,
  Tab,
  Popover,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { recordAgentAction } from "../../../store/agentSlice";
import axiosClient from "../../../api/AxiosClient";
import MarkdownRenderer from "../../MarkdownRenderer";
import CommercialCriteriaTable from "./CommercialCriteriaTable";
import CommercialDataReviewTable from "./CommercialDataReviewTable";
import FinalScoreReviewTable from "./FinalScoreReviewTable";
import styles from "../agent-response.module.scss";
import { v4 as uuid } from "uuid";

// Types
type RankDefinition = {
  label: string;
  rank: number;
};

type EvaluationCriterion = {
  criterion_id: string;
  criterion_name: string;
  description: string;
  weight: number;
  category?: string;
  priority?: "Mandatory" | "Important" | "Desirable";
  scoring_scale?: string;
  scoring_type?: "relative_price" | "relative_rank" | "absolute";
  rank_definitions?: RankDefinition[];
  is_active?: boolean;
};

type VendorScore = {
  criterion_id: string;
  criterion_name: string;
  score: number;
  max_score: number;
  weighted_score: number;
  rating: "High" | "Medium" | "Low" | "Pass" | "Fail";
  justification: string;
  evidence_references: string[];
};

type VendorEvaluation = {
  vendor_id: string;
  vendor_name: string;
  overall_score: number;
  overall_percentage: number;
  ranking: number;
  status: "Passed" | "Failed" | "Conditional";
  scores_by_criterion: VendorScore[];
  strengths: string[];
  weaknesses: string[];
  recommendation: string;
};

// Technical Score Review Types
type TechnicalSubMetric = {
  sub_metric_name: string;
  score: number;
  evidence: string;
  justification: string;
};

type TechnicalCriterion = {
  criterion_name: string;
  category: string;
  score: number;
  max_score: number;
  weight: number;
  weighted_score: number;
  justification: string;
  evidence: string;
  sub_metrics?: TechnicalSubMetric[];
  agent_score?: number;
  effective_score?: number;
  user_score?: number | null;
  is_overridden?: boolean;
  override_reason?: string | null;
  override_history?: any[];
};

type TechnicalVendorScore = {
  vendor_name: string;
  rank: number | null;
  scores: {
    technical: {
      criteria: TechnicalCriterion[];
      total: number;
    };
  };
  summary: {
    strengths: string[];
    weaknesses: string[];
    qualified: boolean;
    rejection_reason?: string;
  };
};

type TechnicalScoreReviewOutput = {
  agent_name: string;
  vendor_scores: TechnicalVendorScore[];
  evaluation_method: string;
  technical_threshold: number;
  qualified_count: number;
  disqualified_count: number;
};

type CommercialExtractionValue = {
  raw_value: string | null;
  normalized_value: number | null;
  unit: string | null;
  currency: string | null;
  mapped_rank: number | null;
  evidence: string | null;
  status: "extracted" | "missing" | "ambiguous" | "needs_mapping";
};

type CommercialInputReviewOutput = {
  agent_name: string;
  step_num: number;
  vendor_inputs: Record<string, Record<string, CommercialExtractionValue>>;
  needs_review_flags: Record<string, string[]>;
  criteria_meta: Record<string, { name: string; scoring_type: string }>;
  workflow_id?: string;
};

// Final Score Review Types (Step 22)
type FinalVendorScore = {
  vendor_name: string;
  rank: number | null;
  final_score: number;
  technical_score_percent: number;
  qualified: boolean;
  scores: {
    technical: { criteria: TechnicalCriterion[]; total: number };
    commercial: { criteria: TechnicalCriterion[]; total: number };
    overall: { final_score: number };
  };
  summary: {
    strengths: string[];
    weaknesses: string[];
    qualified: boolean;
    rejection_reason?: string;
  };
};

type FinalScoreReviewOutput = {
  agent_name: string;
  vendor_scores: FinalVendorScore[];
  evaluation_method: string;
  technical_threshold: number;
  winner: string;
  recommendation: string;
  technical_weight: number;
  commercial_weight: number;
};

// Rubric Approval Types
type RubricCriterion = {
  criterion_id: string;
  criterion_name: string;
  category: string;
  max_score: number;
  score_definitions: {
    [key: string]: string; // e.g., "5": "Excellent: ...", "4": "Good: ...", etc.
  };
};

type RubricApprovalOutput = {
  agent_name: string;
  rubric: {
    technical_rubrics: RubricCriterion[];
    commercial_rubrics: RubricCriterion[];
  };
  total_criteria: number;
  technical_count: number;
  commercial_count: number;
};

type RfqResponseEvaluatorAgentProps = {
  parsedContent: any;
  agentId?: string;
};

export default function RfqResponseEvaluatorAgent({
  parsedContent,
  agentId,
}: RfqResponseEvaluatorAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { selectedAgent } = useSelector((state: RootState) => state.user);
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;

  // State management - MUST be declared before useMemo
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasApproved, setHasApproved] = useState(false);
  const [expandedVendors, setExpandedVendors] = useState<
    Record<string, boolean>
  >({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [rubricActiveTab, setRubricActiveTab] = useState(0);
  const [scorePopoverAnchor, setScorePopoverAnchor] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [editedDefinitions, setEditedDefinitions] = useState<
    Record<string, Record<string, string>>
  >({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedCriterionName, setEditedCriterionName] = useState("");
  const [editedWeightage, setEditedWeightage] = useState("");
  const [editedCriteria, setEditedCriteria] = useState<
    Record<number, Partial<EvaluationCriterion>>
  >({});
  const [editVersion, setEditVersion] = useState(0);
  const [modifiedScores, setModifiedScores] = useState<Record<string, number>>(
    {},
  );
  const [editedTechnicalThreshold, setEditedTechnicalThreshold] = useState<
    number | null
  >(null);
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false);
  const [editedMethod, setEditedMethod] = useState<string | null>(null);
  const [submittedMethod, setSubmittedMethod] = useState<string | null>(null);
  const [editedWeights, setEditedWeights] = useState<{
    tech: number;
    comm: number;
  } | null>(null);
  const [editingScoreKey, setEditingScoreKey] = useState<string | null>(null);
  const [editingScoreValue, setEditingScoreValue] = useState<string>("");
  const [criterionDescPopover, setCriterionDescPopover] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  // Extract data from response
  const result =
    parsedContent?.finalAnswer || parsedContent?.result?.data || parsedContent;

  // Handle nested structure
  let parsedOutput = null;
  let displayMessage = "";
  let agentName = "";

  // RFQ evaluator format: parsedContent.finalAnswer contains {output, prompt}
  // The userSlice already extracts response[0] and stores it in finalAnswer
  let responseData = null;

  if (parsedContent?.finalAnswer) {
    const finalAnswer = parsedContent.finalAnswer;

    // Direct format: finalAnswer = {output: "...", prompt: "..."}
    if (finalAnswer.output && finalAnswer.prompt) {
      responseData = finalAnswer;
    }
    // Old array format (fallback)
    else if (Array.isArray(finalAnswer) && finalAnswer[0]?.output) {
      responseData = finalAnswer[0];
    }
  }

  // Parse the response data
  if (responseData) {
    try {
      parsedOutput = JSON.parse(responseData.output);
    } catch (e) {
      console.error("Failed to parse output JSON:", e);
    }
    displayMessage = responseData.prompt || "";
    agentName = parsedOutput?.agent_name || "";
  }

  // Extract agent_id - the agentId prop comes from msg.agentIdName set by userSlice
  // which gets it from payloadData?.agent_id (the backend's agent_id field)
  // Priority: agentId prop (from userSlice) > agentName from parsedOutput (might be next step)
  const effectiveAgentName = agentId || agentName || "";

  console.log(
    "RFQ Evaluator - Agent:",
    effectiveAgentName,
    "| Parsed Agent:",
    parsedOutput?.agent_name,
    "| Has Data:",
    !!responseData,
    "| Has Message:",
    !!displayMessage,
  );
  console.log("RFQ Evaluator - Rubric Data:", parsedOutput?.rubric);

  // Determine status based on effective agent name
  let status = "";
  if (effectiveAgentName === "document_upload") {
    status = "document_upload";
  } else if (effectiveAgentName === "files_overview") {
    status = "awaiting_files_overview";
  } else if (effectiveAgentName === "criteria_approval" && parsedOutput) {
    status = "awaiting_criteria_review";
  } else if (parsedOutput?.agent_name === "rubric_approval" && parsedOutput) {
    status = "awaiting_rubric_approval";
  } else if (effectiveAgentName === "rubric_approval" && parsedOutput) {
    status = "awaiting_rubric_approval";
  } else if (effectiveAgentName === "method_selection" && parsedOutput) {
    status = "awaiting_method_selection";
  } else if (effectiveAgentName === "vendor_assignment" && parsedOutput) {
    status = "awaiting_vendor_assignment";
  } else if (
    parsedOutput?.agent_name === "technical_score_review" &&
    parsedOutput
  ) {
    status = "awaiting_technical_score_review";
  } else if (effectiveAgentName === "score_review" && parsedOutput) {
    status = "awaiting_score_review";
  } else if (effectiveAgentName === "evaluation_complete" && parsedOutput) {
    status = "completed";
  } else if (effectiveAgentName === "export_response" && parsedOutput) {
    status = "export_ready";
  } else if (effectiveAgentName === "summary_response") {
    status = "summary";
  }

  const message = displayMessage || result?.message || "";

  // Extract download URL from markdown message if present
  const extractedDownloadUrl = useMemo(() => {
    if (!message) return "";

    // Match URL in code block or plain URL after "Download Technical Evaluation Workbook"
    const urlMatch =
      message.match(/```\s*(https:\/\/[^\s]+\.xlsx[^\s]*)\s*```/i) ||
      message.match(
        /Download Technical Evaluation Workbook[^:]*:\s*```\s*(https:\/\/[^\s]+)\s*```/i,
      ) ||
      message.match(/(https:\/\/[^\s]+\.xlsx[^\s]*)/i);

    return urlMatch ? urlMatch[1].trim() : "";
  }, [message]);

  // Extract session IDs from parsedContent (userSlice stores them here)
  const sessionId = parsedContent?.requestId || "";
  const workflowId =
    parsedOutput?.workflow_id || parsedContent?.workflowId || "";
  const totalCriteria =
    parsedOutput?.criteria_summary?.total_criteria ||
    parsedOutput?.total_criteria ||
    0;
  const technicalCount =
    parsedOutput?.criteria_summary?.technical_count ||
    parsedOutput?.technical_count ||
    0;
  const commercialCount =
    parsedOutput?.criteria_summary?.commercial_count ||
    parsedOutput?.commercial_count ||
    0;
  const evaluationMethod = parsedOutput?.evaluation_method || "";
  const winner = parsedOutput?.winner || "";
  const recommendation = parsedOutput?.recommendation || "";
  const evaluationDate = parsedOutput?.evaluation_date || "";

  // Map criteria from new format to existing format
  // Memoize to prevent infinite re-renders in useEffect
  const criteria: EvaluationCriterion[] = useMemo(() => {
    try {
      if (parsedOutput?.criteria) {
        const mappedCriteria = parsedOutput.criteria.map(
          (c: any, index: number) => {
            // Generate criterion_id if not provided
            const categoryPrefix =
              c.category === "commercial" ? "COMM" : "TECH";
            const criterionId =
              c.criterion_id ||
              `${categoryPrefix}-${String(index + 1).padStart(3, "0")}`;

            const baseCriterion = {
              criterion_id: criterionId,
              criterion_name: c.criterion_name,
              description: c.description,
              weight: c.weight, // Already an integer percentage from backend
              category: c.category,
              priority: c.priority,
              scoring_scale: c.scoring_guide || `0-${c.max_score}`,
              scoring_type: c.scoring_type,
              rank_definitions: c.rank_definitions || [],
              is_active: c.is_active !== undefined ? c.is_active : true,
            };

            // Merge with edited values if they exist
            return editedCriteria[index]
              ? { ...baseCriterion, ...editedCriteria[index] }
              : baseCriterion;
          },
        );
        console.log("Mapped criteria:", mappedCriteria);
        return mappedCriteria;
      } else {
        console.log("Using fallback criteria:", result?.criteria || []);
        return result?.criteria || [];
      }
    } catch (error) {
      console.error("Error in criteria useMemo:", error);
      return [];
    }
  }, [parsedContent?.finalAnswer, editVersion]);

  // Map vendor scores from backend format to UI format
  const vendors: VendorEvaluation[] = useMemo(() => {
    if (
      parsedOutput?.vendor_scores &&
      Array.isArray(parsedOutput.vendor_scores)
    ) {
      return parsedOutput.vendor_scores.map((vendor: any) => {
        // Combine technical and commercial scores
        const allScores = [
          ...(vendor.technical_scores || []),
          ...(vendor.commercial_scores || []),
        ].map((score: any) => ({
          criterion_id: score.criterion_id || "",
          criterion_name: score.criterion_name,
          score: score.score,
          max_score: score.max_score,
          weighted_score: score.weighted_score,
          rating:
            score.score >= 7 ? "High" : score.score >= 4 ? "Medium" : "Low",
          justification: score.justification || "",
          evidence_references: score.evidence ? [score.evidence] : [],
        }));

        return {
          vendor_id: vendor.vendor_id || vendor.vendor_name,
          vendor_name: vendor.vendor_name,
          overall_score: vendor.final_score || 0,
          overall_percentage: vendor.final_score || 0,
          ranking: vendor.rank || 0,
          status:
            vendor.qualified === true
              ? "Passed"
              : vendor.qualified === false
                ? "Failed"
                : "Conditional",
          scores_by_criterion: allScores,
          strengths: vendor.strengths || [],
          weaknesses: vendor.weaknesses || [],
          recommendation:
            vendor.rejection_reason ||
            `Technical: ${vendor.total_technical_score?.toFixed(1)}, Commercial: ${vendor.total_commercial_score?.toFixed(1)}`,
        };
      });
    }
    return result?.vendors || [];
  }, [parsedOutput?.vendor_scores]);

  const recommendedVendor = winner || result?.recommended_vendor || "";
  const downloadUrl =
    parsedOutput?.download_url ||
    extractedDownloadUrl ||
    result?.download_url ||
    "";
  const fileSizeKb = parsedOutput?.file_size_kb || 0;
  const progress = result?.progress || 0;

  console.log("RFQ Evaluator Status:", {
    status,
    hasDownloadUrl: !!downloadUrl,
    downloadUrlPreview: downloadUrl?.substring(0, 80) + "...",
    messagePreview: message?.substring(0, 100),
    fileSizeKb,
    parsedOutputKeys: parsedOutput ? Object.keys(parsedOutput) : [],
  });

  // Calculate total percentage weightage
  const totalPercentage = useMemo(() => {
    return criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
  }, [criteria]);

  const isWeightageBalanced = totalPercentage === 100;

  // Event handlers
  const handleFilesOverviewResponse = async (action: "approve" | "reject") => {
    setIsSubmitting(true);
    if (action === "approve") {
      setHasApproved(true);
    }
    try {
      const questionId = uuid();
      const timestamp = new Date().toISOString();

      // Build modify_array with only the edited criteria if action is approve
      let modifyArray: any[] = [];
      if (action === "approve" && Object.keys(editedCriteria).length > 0) {
        modifyArray = Object.keys(editedCriteria).map((indexStr) => {
          const index = parseInt(indexStr);
          const criterion = criteria[index];
          const edits = editedCriteria[index];

          return {
            criterion_id: criterion.criterion_id,
            criterion_name:
              edits.criterion_name !== undefined
                ? edits.criterion_name
                : criterion.criterion_name,
            description: criterion.description,
            weight:
              (edits.weight !== undefined ? edits.weight : criterion.weight) /
              100, // Convert percentage back to decimal
            max_score: 5.0,
            category: criterion.category,
            scoring_guide: criterion.scoring_scale,
          };
        });
      }

      // Add technical_threshold to modify_array if edited and approving
      if (action === "approve" && editedTechnicalThreshold !== null) {
        modifyArray.push({
          technical_threshold: editedTechnicalThreshold,
        });
      }

      // Send eval_method only when user explicitly changed the dropdown
      if (action === "approve" && editedMethod !== null) {
        modifyArray.push({ eval_method: editedMethod });
      }

      // Add weights only when user explicitly edited them and effective method is not commercial
      // (commercial is always 0/100; sending stale weights would override the backend's enforcement)
      const effectiveMethodForWeights =
        editedMethod ?? parsedOutput?.eval_method;
      if (
        action === "approve" &&
        editedWeights !== null &&
        effectiveMethodForWeights !== "commercial"
      ) {
        modifyArray.push({
          technical_weight: editedWeights.tech,
          commercial_weight: editedWeights.comm,
        });
      }

      await dispatch(
        sendMessageToAgent({
          agentId: selectedAgent?.id,
          message: action,
          questionId,
          timestamp,
          agentEndpoint: "/stream/rfq_response_evaluator",
          sessionId,
          modify_array: modifyArray.length > 0 ? modifyArray : undefined,
        }),
      ).unwrap();
      // Reset all edit state so a follow-up plain approve doesn't re-send stale edits
      setSubmittedMethod(editedMethod ?? parsedOutput?.eval_method ?? null);
      setEditedCriteria({});
      setEditedTechnicalThreshold(null);
      setEditedMethod(null);
      setEditedWeights(null);
    } catch (error) {
      console.error("Error sending files overview response:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCriterion = (index: number) => {
    setEditingIndex(index);
    setEditedCriterionName(criteria[index].criterion_name || "");
    setEditedWeightage(criteria[index].weight.toString());
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null) {
      // Save the edited values to the editedCriteria state
      setEditedCriteria((prev) => ({
        ...prev,
        [editingIndex]: {
          criterion_name: editedCriterionName,
          weight: parseFloat(editedWeightage) || 0,
        },
      }));

      // Increment version to trigger useMemo recalculation
      setEditVersion((v) => v + 1);

      setEditDialogOpen(false);
      setEditingIndex(null);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingIndex(null);
    setEditedCriterionName("");
    setEditedWeightage("");
  };

  const handleThresholdEditClick = () => {
    // Use existing edited value if available, otherwise use original
    if (editedTechnicalThreshold === null) {
      setEditedTechnicalThreshold(parsedOutput?.technical_threshold || 0);
    }
    setThresholdDialogOpen(true);
  };

  const handleThresholdCancel = () => {
    setThresholdDialogOpen(false);
  };

  const handleThresholdSave = () => {
    setThresholdDialogOpen(false);
  };

  const handleConfirmCriteria = async () => {
    setIsSubmitting(true);
    setHasApproved(true);
    try {
      // Build modify_array with edited criteria
      const modifyArray: any[] = Object.keys(editedCriteria).map((indexStr) => {
        const index = parseInt(indexStr);
        const criterion = criteria[index];
        const edits = editedCriteria[index];

        return {
          criterion_id: criterion.criterion_id,
          criterion_name: criterion.criterion_name,
          description: criterion.description,
          weight:
            (edits.weight !== undefined ? edits.weight : criterion.weight) /
            100, // Convert percentage back to decimal
          max_score: 5.0,
          category: criterion.category,
          scoring_guide:
            edits.scoring_scale !== undefined
              ? edits.scoring_scale
              : criterion.scoring_scale,
        };
      });

      // Add technical_threshold to modify_array if edited
      if (editedTechnicalThreshold !== null) {
        modifyArray.push({
          technical_threshold: editedTechnicalThreshold,
        });
      }

      const questionId = uuid();
      const timestamp = new Date().toISOString();

      console.log("Sending approval with modify_array:", modifyArray);

      await dispatch(
        sendMessageToAgent({
          agentId: selectedAgent?.id,
          message: "approve",
          questionId,
          timestamp,
          agentEndpoint: "/stream/rfq_response_evaluator",
          sessionId,
          modify_array: modifyArray.length > 0 ? modifyArray : undefined,
        }),
      ).unwrap();
      // Reset criteria edits so a follow-up plain approve doesn't re-send stale edits
      setEditedCriteria({});
    } catch (error) {
      console.error("Error confirming criteria:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveTechnicalScores = async () => {
    setIsSubmitting(true);
    setHasApproved(true);
    try {
      const questionId = uuid();
      const timestamp = new Date().toISOString();

      // Build modify_array with modified scores
      let modifyArray: any[] = [];
      if (Object.keys(modifiedScores).length > 0 && parsedOutput) {
        const techOutput = parsedOutput as TechnicalScoreReviewOutput;
        const vendors = techOutput.vendor_scores || [];
        const allCriteria =
          vendors.length > 0 && vendors[0].scores?.technical?.criteria
            ? vendors[0].scores.technical.criteria
            : [];

        modifyArray = Object.entries(modifiedScores).map(
          ([key, modifiedScore]) => {
            const [vendorIdxStr, criterionIdxStr] = key.split("_");
            const vendorIdx = parseInt(vendorIdxStr);
            const criterionIdx = parseInt(criterionIdxStr);

            const vendor = vendors[vendorIdx];
            const criterionName =
              allCriteria[criterionIdx]?.criterion_name || "";

            // Find the actual criterion for this vendor
            const vendorCriterion = vendor.scores?.technical?.criteria?.find(
              (c) => c.criterion_name === criterionName,
            );

            return {
              vendor_name: vendor.vendor_name,
              criterion_name: criterionName,
              original_score:
                vendorCriterion?.agent_score ?? vendorCriterion?.score ?? 0,
              new_score: modifiedScore,
            };
          },
        );
      }

      // Add technical_threshold to modify_array if edited
      if (editedTechnicalThreshold !== null) {
        modifyArray.push({
          technical_threshold: editedTechnicalThreshold,
        });
      }

      console.log(
        "Sending technical score approval with modify_array:",
        modifyArray,
      );

      await dispatch(
        sendMessageToAgent({
          agentId: selectedAgent?.id,
          message: "approve",
          questionId,
          timestamp,
          agentEndpoint: "/stream/rfq_response_evaluator",
          sessionId,
          modify_array: modifyArray.length > 0 ? modifyArray : undefined,
        }),
      ).unwrap();
      setModifiedScores({});
      setEditedTechnicalThreshold(null);
    } catch (error) {
      console.error("Error approving technical scores:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveCommercialInputs = async (modifyArray: any[]) => {
    setIsSubmitting(true);
    setHasApproved(true);
    try {
      await dispatch(
        sendMessageToAgent({
          agentId: selectedAgent?.id,
          message: "approve",
          questionId: uuid(),
          timestamp: new Date().toISOString(),
          agentEndpoint: "/stream/rfq_response_evaluator",
          sessionId,
          modify_array: modifyArray.length > 0 ? modifyArray : undefined,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Error approving commercial inputs:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadReport = async (format: "excel" | "pdf") => {
    setIsDownloading(true);
    try {
      const fileName =
        downloadUrl && format === "excel"
          ? `Technical_Evaluation_${workflowId || sessionId}.xlsx`
          : `rfq_evaluation_${sessionId}.${format === "excel" ? "xlsx" : "pdf"}`;

      // Record download action - fire and forget (non-blocking)
      dispatch(
        recordAgentAction({
          agentId: numericAgentId,
          actionType: "download",
          content: JSON.stringify({
            fileName,
            format,
            sessionId,
            workflowId: workflowId || null,
          }),
        }),
      );

      if (downloadUrl && format === "excel") {
        // Use direct download URL from message (for evaluation_completed status)
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback to API endpoint
        const response = await axiosClient.get(
          `/rfq-evaluator/download/${sessionId}?format=${format}`,
          { responseType: "blob" },
        );

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading report:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleVendorExpansion = (vendorId: string) => {
    setExpandedVendors((prev) => ({
      ...prev,
      [vendorId]: !prev[vendorId],
    }));
  };

  const handleOpenScorePopover = (
    key: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setScorePopoverAnchor((prev) => ({
      ...prev,
      [key]: event.currentTarget,
    }));
  };

  const handleCloseScorePopover = (key: string) => {
    setScorePopoverAnchor((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const handleScoreClick = (
    vendorIdx: number,
    criterionIdx: number,
    currentScore: number,
  ) => {
    const key = `${vendorIdx}_${criterionIdx}`;
    setEditingScoreKey(key);
    setEditingScoreValue(currentScore.toFixed(1));
  };

  const handleScoreSave = () => {
    if (editingScoreKey) {
      const newScore = parseFloat(editingScoreValue);
      if (!isNaN(newScore) && newScore >= 0 && newScore <= 5) {
        // Round to 1 decimal place
        const roundedScore = Math.round(newScore * 10) / 10;
        setModifiedScores((prev) => ({
          ...prev,
          [editingScoreKey]: roundedScore,
        }));
      }
      setEditingScoreKey(null);
      setEditingScoreValue("");
    }
  };

  const handleScoreCancel = () => {
    setEditingScoreKey(null);
    setEditingScoreValue("");
  };

  const handleOpenCriterionDesc = (
    key: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setCriterionDescPopover((prev) => ({
      ...prev,
      [key]: event.currentTarget,
    }));
  };

  const handleCloseCriterionDesc = (key: string) => {
    setCriterionDescPopover((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const getRatingStyle = (rating: string) => {
    switch (rating) {
      case "High":
      case "Pass":
        return { bg: "#e8f5e9", color: "#2e7d32" };
      case "Medium":
        return { bg: "#fff3e0", color: "#e65100" };
      case "Low":
      case "Fail":
        return { bg: "#ffebee", color: "#c62828" };
      default:
        return { bg: "#f5f5f5", color: "#666" };
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "Passed":
        return { bg: "#e8f5e9", color: "#2e7d32" };
      case "Conditional":
        return { bg: "#fff3e0", color: "#e65100" };
      case "Failed":
        return { bg: "#ffebee", color: "#c62828" };
      default:
        return { bg: "#f5f5f5", color: "#666" };
    }
  };
  // After approval, show the method that was actually submitted rather than reverting to the backend default
  const resolvedMethod =
    (hasApproved ? submittedMethod : editedMethod) ??
    parsedOutput?.eval_method ??
    "";

  console.log("PARSED OUTPYT", parsedOutput);
  return (
    <div className={styles.pressReleaseContent}>
      {/* Document Upload Instructions */}
      {status === "document_upload" && message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              "& h1, & h2, & h3": {
                fontSize: "18px",
                fontWeight: 600,
                color: "#004141",
                mb: 2,
                mt: 2,
              },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Parsing/Uploading Status */}
      {(status === "parsing" || status === "uploading") && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <UploadFileIcon sx={{ color: "#00a888", fontSize: 32 }} />
            <Typography
              sx={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
            >
              {message || "Processing documents..."}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "#d0f2ed",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#00a888",
              },
            }}
          />
          <Typography
            sx={{ fontSize: "13px", color: "#666", mt: 1, textAlign: "center" }}
          >
            {progress}%
          </Typography>
        </Paper>
      )}

      {/* Files Overview (HITL) - NEW: First gate before criteria review */}
      {status === "awaiting_files_overview" && message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          {/* Evaluation Criteria Table removed — criteria are reviewed/edited at step 1 (criteria_approval) */}
          {false && criteria.length > 0 && (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                mt: 3,
                mb: 3,
                border: "1px solid #b2e5de",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0fbf9" }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#004141",
                        fontSize: "14px",
                        borderBottom: "2px solid #b2e5de",
                      }}
                    >
                      Criteria
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#004141",
                        fontSize: "14px",
                        borderBottom: "2px solid #b2e5de",
                        textAlign: "center",
                      }}
                    >
                      Percentage Weightage
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#004141",
                        fontSize: "14px",
                        borderBottom: "2px solid #b2e5de",
                        textAlign: "center",
                        width: "80px",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {criteria.map((criterion, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                        "&:hover": { backgroundColor: "#f0fbf9" },
                      }}
                    >
                      <TableCell sx={{ fontSize: "14px", color: "#2a312b" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {criterion.criterion_name}
                          {criterion.description && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  handleOpenCriterionDesc(
                                    `criterion_${index}`,
                                    e,
                                  )
                                }
                                sx={{
                                  padding: "4px",
                                  color: "#1976d2",
                                  "&:hover": {
                                    backgroundColor: "#e3f2fd",
                                  },
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <Popover
                                open={Boolean(
                                  criterionDescPopover[`criterion_${index}`],
                                )}
                                anchorEl={
                                  criterionDescPopover[`criterion_${index}`]
                                }
                                onClose={() =>
                                  handleCloseCriterionDesc(`criterion_${index}`)
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                              >
                                <Paper sx={{ p: 2, maxWidth: 400 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#004141",
                                      mb: 1,
                                    }}
                                  >
                                    {criterion.criterion_name}
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "13px", color: "#2a312b" }}
                                  >
                                    {criterion.description}
                                  </Typography>
                                </Paper>
                              </Popover>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "14px",
                          color: "#2a312b",
                          textAlign: "center",
                        }}
                      >
                        <Chip
                          label={`${(criterion.weight || 0).toFixed(0)}%`}
                          sx={{
                            backgroundColor: "#004141",
                            color: "#ffffff",
                            fontWeight: 600,
                            fontSize: "12px",
                            height: "26px",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditCriterion(index)}
                          disabled={isSubmitting || hasApproved}
                          sx={{
                            color: "#004141",
                            "&:hover": { backgroundColor: "#f0fbf9" },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ backgroundColor: "#f0fbf9" }}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#004141",
                        fontSize: "14px",
                        borderTop: "2px solid #b2e5de",
                        textAlign: "right",
                        paddingRight: 3,
                      }}
                    >
                      Total Percentage
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#004141",
                        fontSize: "14px",
                        borderTop: "2px solid #b2e5de",
                        textAlign: "center",
                      }}
                    >
                      <Chip
                        label={`${totalPercentage.toFixed(0)}%`}
                        sx={{
                          backgroundColor: isWeightageBalanced
                            ? "#4caf50"
                            : "#f44336",
                          color: "#ffffff",
                          fontWeight: 700,
                          fontSize: "13px",
                          height: "28px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderTop: "2px solid #b2e5de",
                      }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}

          {/* Evaluation Method Info */}
          {parsedOutput?.eval_method && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: "#e8f5e9",
                border: "1px solid #4caf50",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "max-content auto",
                  alignItems: "center",
                  rowGap: 1.5,
                  columnGap: 2,
                }}
              >
                {/* Row 1: Evaluation method */}
                {/* Fix #2: my:0 removes <p> default browser margins that don't collapse in Grid */}
                {/* Fix #3: font size normalised to 13px to match other row labels */}
                {/* Fix #4: id used by Select's labelId for screen-reader association */}
                <Typography
                  id="eval-method-label"
                  component="span"
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#2e7d32",
                    my: 0,
                  }}
                >
                  Evaluation:
                </Typography>
                <FormControl
                  size="small"
                  disabled={isSubmitting || hasApproved}
                  sx={{ width: "fit-content" }}
                >
                  {/* Fix #4: labelId links Select to the Typography above */}
                  {/* Fix #1: value falls back to "" so MUI never sees an unmatched value */}
                  <Select
                    labelId="eval-method-label"
                    value={resolvedMethod}
                    onChange={(e) => setEditedMethod(e.target.value)}
                    sx={{
                      fontSize: "13px",
                      color: "#1b5e20",
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4caf50",
                      },
                    }}
                  >
                    {(() => {
                      const labels: Record<string, string> = {
                        technical_threshold: "Technical Only",
                        techno_commercial: "Techno-Commercial",
                        commercial:
                          "Technically Acceptable Commercially Lowest",
                      };
                      const currentMethod = resolvedMethod;
                      const isTechnicalOnly =
                        currentMethod === "technical_threshold";
                      const fallback = isTechnicalOnly
                        ? ["technical_threshold"]
                        : ["commercial", "techno_commercial"];
                      // Fix #1: guarantee currentMethod is always present in the list so
                      // MUI can match the value and render the selected option text
                      const base: string[] =
                        parsedOutput.available_methods ?? fallback;
                      const methods: string[] =
                        currentMethod && !base.includes(currentMethod)
                          ? [currentMethod, ...base]
                          : base;
                      return methods.map((method: string) => (
                        <MenuItem key={method} value={method}>
                          {labels[method] ??
                            method
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </MenuItem>
                      ));
                    })()}
                  </Select>
                </FormControl>

                {/* Row 2: Technical Cut-off Score */}
                {parsedOutput.technical_threshold !== undefined && (
                  <>
                    {/* Fix #2: component="span" + my:0 — avoids <p> margins inside Grid */}
                    <Typography
                      component="span"
                      sx={{ fontSize: "13px", color: "#1b5e20", my: 0 }}
                    >
                      Technical Cut-off Score:
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        component="span"
                        sx={{ fontSize: "13px", color: "#1b5e20", my: 0 }}
                      >
                        {editedTechnicalThreshold !== null
                          ? editedTechnicalThreshold
                          : parsedOutput.technical_threshold}
                        %
                      </Typography>
                      {/* Fix #5: aria-label gives screen readers a meaningful button name */}
                      {resolvedMethod !== "commercial" && (
                        <IconButton
                          size="small"
                          aria-label="Edit technical cut-off score"
                          onClick={handleThresholdEditClick}
                          disabled={isSubmitting || hasApproved}
                          sx={{
                            color: "#1b5e20",
                            "&:hover": { backgroundColor: "#c8e6c9" },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </>
                )}

                {/* Row 3: Weights */}
                {parsedOutput.technical_weight !== undefined &&
                  parsedOutput.commercial_weight !== undefined &&
                  resolvedMethod !== "commercial" && (
                    <>
                      {/* Fix #2: component="span" + my:0 */}
                      <Typography
                        component="span"
                        sx={{ fontSize: "13px", color: "#1b5e20", my: 0 }}
                      >
                        Weights:
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TextField
                          type="number"
                          size="small"
                          value={
                            editedWeights !== null
                              ? editedWeights.tech
                              : parsedOutput.technical_weight
                          }
                          onChange={(e) => {
                            const tech = Number(e.target.value);
                            setEditedWeights({ tech, comm: 100 - tech });
                          }}
                          disabled={isSubmitting || hasApproved}
                          inputProps={{ min: 0, max: 100, step: 5 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography
                                  component="span"
                                  sx={{ fontSize: "13px", my: 0 }}
                                >
                                  % Tech
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: 130 }}
                        />
                        <TextField
                          type="number"
                          size="small"
                          value={
                            editedWeights !== null
                              ? editedWeights.comm
                              : parsedOutput.commercial_weight
                          }
                          onChange={(e) => {
                            const comm = Number(e.target.value);
                            setEditedWeights({ tech: 100 - comm, comm });
                          }}
                          disabled={isSubmitting || hasApproved}
                          inputProps={{ min: 0, max: 100, step: 5 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography
                                  component="span"
                                  sx={{ fontSize: "13px", my: 0 }}
                                >
                                  % Comm
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: 140 }}
                        />
                      </Box>
                    </>
                  )}
              </Box>
            </Paper>
          )}

          {/* Display the markdown prompt/message below the evaluation box */}
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              mt: 2,
              "& h1": {
                fontSize: "20px",
                fontWeight: 600,
                color: "#004141",
                mb: 2,
              },
              "& h2": {
                fontSize: "16px",
                fontWeight: 600,
                color: "#004141",
                mb: 1.5,
                mt: 2,
              },
              "& h3": {
                fontSize: "14px",
                fontWeight: 600,
                color: "#004141",
                mb: 1,
                mt: 1.5,
              },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
              "& hr": {
                border: "none",
                borderTop: "1px solid #b2e5de",
                margin: "16px 0",
              },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
          >
            <Button
              variant="outlined"
              onClick={() => handleFilesOverviewResponse("reject")}
              disabled={isSubmitting || hasApproved}
              sx={{
                borderColor: "#d32f2f",
                color: "#d32f2f",
                "&:hover": {
                  borderColor: "#c62828",
                  backgroundColor: "#ffebee",
                },
                textTransform: "none",
                fontWeight: 600,
                px: 4,
              }}
            >
              Reject
            </Button>
            <Tooltip
              title={!isWeightageBalanced ? "Total weights should be 100%" : ""}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={() => handleFilesOverviewResponse("approve")}
                  disabled={isSubmitting || hasApproved || !isWeightageBalanced}
                  sx={{
                    backgroundColor: "#00a888",
                    "&:hover": { backgroundColor: "#008f75" },
                    "&.Mui-disabled": {
                      backgroundColor: "#ccc",
                      color: "#666",
                    },
                    textTransform: "none",
                    fontWeight: 600,
                    px: 4,
                  }}
                >
                  {isSubmitting
                    ? Object.keys(editedCriteria).length > 0
                      ? "Submitting..."
                      : "Approving..."
                    : Object.keys(editedCriteria).length > 0
                      ? "Submit"
                      : "Approve"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      )}

      {/* Criteria Review (HITL) — commercial Gate 2 */}
      {status === "awaiting_criteria_review" && criteria.length > 0 && (
        <CommercialCriteriaTable
          criteria={criteria}
          commercialWeight={parsedOutput?.commercial_weight ?? 0}
          technicalWeight={parsedOutput?.technical_weight ?? 0}
          message={message}
          isSubmitting={isSubmitting}
          hasApproved={hasApproved}
          onEditCriterion={handleEditCriterion}
          onConfirm={handleConfirmCriteria}
          totalPercentage={totalPercentage}
          isWeightageBalanced={isWeightageBalanced}
          header={parsedOutput?.header}
        />
      )}

      {/* Rubric Approval */}
      {status === "awaiting_rubric_approval" &&
        parsedOutput &&
        (() => {
          const rubricOutput = parsedOutput as RubricApprovalOutput;
          const technicalRubrics = rubricOutput.rubric?.technical_rubrics || [];
          const commercialRubrics =
            rubricOutput.rubric?.commercial_rubrics || [];

          // Create tabs for each individual criterion
          const allCriteria = [
            ...technicalRubrics.map((criterion) => ({
              ...criterion,
              category: "Technical",
            })),
            ...commercialRubrics.map((criterion) => ({
              ...criterion,
              category: "Commercial",
            })),
          ];

          const handleTabChange = (
            _event: React.SyntheticEvent,
            newValue: number,
          ) => {
            setRubricActiveTab(newValue);
          };

          const handleApprove = async () => {
            setIsSubmitting(true);
            setHasApproved(true);
            try {
              const questionId = uuid();
              const timestamp = new Date().toISOString();

              // Build modify_array with edited rubric definitions
              let modifyArray: any[] = [];
              if (Object.keys(editedDefinitions).length > 0) {
                modifyArray = Object.entries(editedDefinitions)
                  .map(([criterionId, scoreEdits]) => {
                    // Find the criterion from either technical or commercial rubrics
                    const criterion = allCriteria.find(
                      (c) => c.criterion_id === criterionId,
                    );
                    if (!criterion) return null;

                    // Merge original score_definitions with edited ones
                    const updatedDefinitions = {
                      ...criterion.score_definitions,
                      ...scoreEdits,
                    };

                    return {
                      criterion_id: criterionId,
                      criterion_name: criterion.criterion_name,
                      category: criterion.category,
                      max_score: criterion.max_score,
                      score_definitions: updatedDefinitions,
                    };
                  })
                  .filter(Boolean); // Remove null entries
              }

              // Add technical_threshold to modify_array if edited
              if (editedTechnicalThreshold !== null) {
                modifyArray.push({
                  technical_threshold: editedTechnicalThreshold,
                });
              }

              console.log(
                "Sending rubric approval with modifications:",
                modifyArray,
              );

              await dispatch(
                sendMessageToAgent({
                  agentId: selectedAgent?.id,
                  message: "approve",
                  questionId,
                  timestamp,
                  agentEndpoint: "/stream/rfq_response_evaluator",
                  sessionId,
                  modify_array:
                    modifyArray.length > 0 ? modifyArray : undefined,
                }),
              ).unwrap();
              setEditedDefinitions({});
              setEditedTechnicalThreshold(null);
            } catch (error) {
              console.error("Error approving rubric:", error);
            } finally {
              setIsSubmitting(false);
            }
          };

          return (
            <>
              {/* Header Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: "#f0fbf9",
                  border: "1px solid #b2e5de",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "#004141",
                    fontWeight: 600,
                    mb: 0,
                  }}
                >
                  Scoring Grid
                </Typography>
              </Paper>

              {/* Vertical Tabs Section */}
              {allCriteria.length > 0 && (
                <>
                  <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                    {/* Left Sidebar - Vertical Tabs */}
                    <Box
                      sx={{
                        minWidth: "280px",
                        maxWidth: "280px",
                        borderRight: "2px solid #b2e5de",
                        pr: 2,
                      }}
                    >
                      <Tabs
                        value={rubricActiveTab}
                        onChange={handleTabChange}
                        orientation="vertical"
                        sx={{
                          "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2a312b",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            textAlign: "left",
                            padding: "10px 16px",
                            borderRadius: "8px",
                            mb: 1,
                            minHeight: "auto",
                            transition: "all 0.2s",
                            "&:hover": {
                              backgroundColor: "#f0fbf9",
                            },
                            "&.Mui-selected": {
                              color: "#00a888",
                              backgroundColor: "#d0f2ed",
                              fontWeight: 600,
                            },
                          },
                          "& .MuiTabs-indicator": {
                            backgroundColor: "#00a888",
                            width: "4px",
                            left: 0,
                          },
                        }}
                      >
                        {allCriteria.map((criterion) => (
                          <Tab
                            key={criterion.criterion_id}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: "inherit",
                                    lineHeight: 1.4,
                                    margin: 0,
                                  }}
                                >
                                  {criterion.criterion_name}
                                </Typography>
                              </Box>
                            }
                          />
                        ))}
                      </Tabs>
                    </Box>

                    {/* Right Content Area */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {allCriteria.map(
                        (criterion, index) =>
                          rubricActiveTab === index && (
                            <Paper
                              key={criterion.criterion_id}
                              elevation={0}
                              sx={{
                                p: 3,
                                border: "1px solid #b2e5de",
                                borderRadius: "8px",
                                backgroundColor: "#ffffff",
                              }}
                            >
                              {/* Criterion Header */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  mb: 3,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: "#004141",
                                      fontWeight: 600,
                                      fontSize: "20px",
                                      mb: 1,
                                    }}
                                  >
                                    {criterion.criterion_name}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={`Max Score: ${criterion.max_score}`}
                                  sx={{
                                    backgroundColor: "#00a888",
                                    color: "#ffffff",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                  }}
                                />
                              </Box>

                              {/* Score Definitions */}
                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    color: "#004141",
                                    fontWeight: 600,
                                    mb: 2,
                                    fontSize: "15px",
                                  }}
                                >
                                  Score Definitions:
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                  }}
                                >
                                  {Object.entries(criterion.score_definitions)
                                    .sort(([a], [b]) => Number(b) - Number(a))
                                    .map(([score, definition]) => {
                                      const currentValue =
                                        editedDefinitions[
                                          criterion.criterion_id
                                        ]?.[score] || definition;

                                      return (
                                        <Box
                                          key={score}
                                          sx={{
                                            display: "flex",
                                            gap: 2,
                                            alignItems: "flex-start",
                                          }}
                                        >
                                          <Chip
                                            label={score}
                                            sx={{
                                              backgroundColor: "#00a888",
                                              color: "#ffffff",
                                              fontWeight: 700,
                                              fontSize: "16px",
                                              minWidth: "50px",
                                              height: "40px",
                                            }}
                                          />
                                          <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            value={currentValue}
                                            disabled={
                                              isSubmitting || hasApproved
                                            }
                                            onChange={(e) => {
                                              setEditedDefinitions((prev) => ({
                                                ...prev,
                                                [criterion.criterion_id]: {
                                                  ...prev[
                                                    criterion.criterion_id
                                                  ],
                                                  [score]: e.target.value,
                                                },
                                              }));
                                            }}
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                backgroundColor: "#ffffff",
                                                fontSize: "14px",
                                                "& fieldset": {
                                                  borderColor: "#b2e5de",
                                                },
                                                "&:hover fieldset": {
                                                  borderColor: "#00a888",
                                                },
                                                "&.Mui-focused fieldset": {
                                                  borderColor: "#00a888",
                                                },
                                              },
                                            }}
                                          />
                                        </Box>
                                      );
                                    })}
                                </Box>
                              </Box>
                            </Paper>
                          ),
                      )}
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleApprove}
                      disabled={isSubmitting || hasApproved}
                      sx={{
                        backgroundColor: "#00a888",
                        "&:hover": { backgroundColor: "#008f75" },
                        textTransform: "none",
                        fontWeight: 600,
                        px: 4,
                      }}
                    >
                      {isSubmitting
                        ? Object.keys(editedDefinitions).length > 0
                          ? "Submitting..."
                          : "Approving..."
                        : Object.keys(editedDefinitions).length > 0
                          ? "Submit"
                          : "Approve"}
                    </Button>
                  </Box>
                </>
              )}
            </>
          );
        })()}

      {/* Method Selection */}
      {status === "awaiting_method_selection" && message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              "& h1": {
                fontSize: "20px",
                fontWeight: 600,
                color: "#004141",
                mb: 2,
              },
              "& h2": {
                fontSize: "16px",
                fontWeight: 600,
                color: "#004141",
                mb: 1.5,
                mt: 2,
              },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
              "& hr": {
                border: "none",
                borderTop: "1px solid #b2e5de",
                margin: "16px 0",
              },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Vendor Assignment */}
      {status === "awaiting_vendor_assignment" && message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              "& h1": {
                fontSize: "20px",
                fontWeight: 600,
                color: "#004141",
                mb: 2,
              },
              "& h2": {
                fontSize: "16px",
                fontWeight: 600,
                color: "#004141",
                mb: 1.5,
                mt: 2,
              },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
              "& hr": {
                border: "none",
                borderTop: "1px solid #b2e5de",
                margin: "16px 0",
              },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Score Review — Gate 21: Commercial Data Review Table */}
      {status === "awaiting_score_review" &&
        parsedOutput &&
        (parsedOutput as CommercialInputReviewOutput).vendor_inputs && (
          <CommercialDataReviewTable
            vendorInputs={
              (parsedOutput as CommercialInputReviewOutput).vendor_inputs
            }
            criteriaMeta={
              (parsedOutput as CommercialInputReviewOutput).criteria_meta || {}
            }
            needsReviewFlags={
              (parsedOutput as CommercialInputReviewOutput)
                .needs_review_flags || {}
            }
            message={message || ""}
            isSubmitting={isSubmitting}
            hasApproved={hasApproved}
            onApproveWithEdits={handleApproveCommercialInputs}
          />
        )}

      {/* Score Review — Step 22: Final techno-commercial score review table */}
      {status === "awaiting_score_review" &&
        parsedOutput &&
        (parsedOutput as FinalScoreReviewOutput).vendor_scores &&
        !(parsedOutput as CommercialInputReviewOutput).vendor_inputs && (
          <FinalScoreReviewTable
            vendors={(parsedOutput as FinalScoreReviewOutput).vendor_scores}
            technicalWeight={
              (parsedOutput as FinalScoreReviewOutput).technical_weight ?? 0
            }
            commercialWeight={
              (parsedOutput as FinalScoreReviewOutput).commercial_weight ?? 0
            }
            winner={(parsedOutput as FinalScoreReviewOutput).winner ?? ""}
            recommendation={
              (parsedOutput as FinalScoreReviewOutput).recommendation ?? ""
            }
            message={message || ""}
            isSubmitting={isSubmitting}
            hasApproved={hasApproved}
            onApprove={() => handleApproveCommercialInputs([])}
          />
        )}

      {/* Technical Score Review - Table Format */}
      {status === "awaiting_technical_score_review" &&
        parsedOutput &&
        (() => {
          const techOutput = parsedOutput as TechnicalScoreReviewOutput;
          const vendors = techOutput.vendor_scores || [];
          const allCriteria =
            vendors.length > 0 && vendors[0].scores?.technical?.criteria
              ? vendors[0].scores.technical.criteria
              : [];

          return (
            <>
              {/* Technical Evaluation Complete Header */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: "#f0fbf9",
                  border: "1px solid #b2e5de",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "#004141",
                    fontWeight: 600,
                    mb: 0,
                  }}
                >
                  Technical Evaluation Completed
                </Typography>
              </Paper>

              {/* Summary Header - Hidden as per user request */}
              {/* <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: "#f0fbf9",
                border: "1px solid #b2e5de",
                borderRadius: "8px",
              }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#004141", mb: 2 }}>
                Technical Evaluation Summary
              </Typography>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Box>
                  <Typography sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>
                    Evaluation Method
                  </Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#004141" }}>
                    {techOutput.evaluation_method || "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>
                    Technical Threshold
                  </Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#004141" }}>
                    {techOutput.technical_threshold || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>
                    Qualified Vendors
                  </Typography>
                  <Chip
                    label={techOutput.qualified_count || 0}
                    size="small"
                    sx={{
                      backgroundColor: "#4caf50",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>
                    Disqualified Vendors
                  </Typography>
                  <Chip
                    label={techOutput.disqualified_count || 0}
                    size="small"
                    sx={{
                      backgroundColor: "#f44336",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>
            </Paper> */}

              {/* Vendors Table - Transposed Format (Criteria as Rows) */}
              {vendors.length > 0 ? (
                <>
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 3,
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table sx={{ borderCollapse: "collapse" }}>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              rowSpan={2}
                              sx={{
                                backgroundColor: "#f5f5f5",
                                fontWeight: 600,
                                color: "#333",
                                fontSize: "14px",
                                padding: "16px",
                                borderBottom: "2px solid #e0e0e0",
                                borderRight: "1px solid #e0e0e0",
                                minWidth: 250,
                                verticalAlign: "middle",
                              }}
                            >
                              Criteria
                            </TableCell>
                            {vendors.map((vendor, idx) => {
                              const isQualified =
                                vendor.summary?.qualified !== false;
                              // Check both local state modifications and backend modifications
                              const hasLocalModifications = allCriteria.some(
                                (criterion, criterionIdx) => {
                                  const vendorCriterion =
                                    vendor.scores?.technical?.criteria?.find(
                                      (c) =>
                                        c.criterion_name ===
                                        criterion.criterion_name,
                                    );
                                  const originalScore =
                                    vendorCriterion?.agent_score ??
                                    vendorCriterion?.score ??
                                    0;
                                  const scoreKey = `${idx}_${criterionIdx}`;
                                  const modifiedScore =
                                    modifiedScores[scoreKey];
                                  return (
                                    modifiedScore !== undefined &&
                                    modifiedScore !== originalScore
                                  );
                                },
                              );
                              const hasBackendModifications =
                                vendor.scores?.technical?.criteria?.some(
                                  (criterion) =>
                                    criterion.user_score !== null ||
                                    criterion.is_overridden,
                                );
                              const hasModifiedScores =
                                hasLocalModifications ||
                                hasBackendModifications;
                              const colspan = hasModifiedScores ? 3 : 2;
                              return (
                                <TableCell
                                  key={idx}
                                  colSpan={colspan}
                                  align="center"
                                  sx={{
                                    backgroundColor: isQualified
                                      ? "#c8e6c9"
                                      : "#ffcdd2",
                                    fontWeight: 600,
                                    color: "#333",
                                    fontSize: "14px",
                                    padding: "16px",
                                    borderBottom: "1px solid #e0e0e0",
                                    borderRight:
                                      idx < vendors.length - 1
                                        ? "1px solid #e0e0e0"
                                        : "none",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {vendor.vendor_name}
                                    {isQualified && (
                                      <CheckCircleIcon
                                        sx={{ color: "#2e7d32", fontSize: 20 }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                          <TableRow>
                            {vendors.map((vendor, vendorIdx) => {
                              const isQualified =
                                vendor.summary?.qualified !== false;
                              // Check both local state modifications and backend modifications
                              const hasLocalModifications = allCriteria.some(
                                (criterion, criterionIdx) => {
                                  const vendorCriterion =
                                    vendor.scores?.technical?.criteria?.find(
                                      (c) =>
                                        c.criterion_name ===
                                        criterion.criterion_name,
                                    );
                                  const originalScore =
                                    vendorCriterion?.agent_score ??
                                    vendorCriterion?.score ??
                                    0;
                                  const scoreKey = `${vendorIdx}_${criterionIdx}`;
                                  const modifiedScore =
                                    modifiedScores[scoreKey];
                                  return (
                                    modifiedScore !== undefined &&
                                    modifiedScore !== originalScore
                                  );
                                },
                              );
                              const hasBackendModifications =
                                vendor.scores?.technical?.criteria?.some(
                                  (criterion) =>
                                    criterion.user_score !== null ||
                                    criterion.is_overridden,
                                );
                              const hasModifiedScores =
                                hasLocalModifications ||
                                hasBackendModifications;
                              return (
                                <React.Fragment key={vendorIdx}>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      backgroundColor: isQualified
                                        ? "#e8f5e9"
                                        : "#ffebee",
                                      fontWeight: 600,
                                      color: "#333",
                                      fontSize: "13px",
                                      padding: "12px 16px",
                                      borderBottom: "2px solid #e0e0e0",
                                      borderRight: "1px solid #e0e0e0",
                                      minWidth: 100,
                                    }}
                                  >
                                    Agent Score
                                  </TableCell>
                                  {hasModifiedScores && (
                                    <TableCell
                                      align="center"
                                      sx={{
                                        backgroundColor: "#FFFACD",
                                        fontWeight: 600,
                                        color: "#000",
                                        fontSize: "13px",
                                        padding: "12px 16px",
                                        borderBottom: "2px solid #e0e0e0",
                                        borderRight: "1px solid #e0e0e0",
                                        minWidth: 100,
                                      }}
                                    >
                                      User Modified Score
                                    </TableCell>
                                  )}
                                  <TableCell
                                    align="center"
                                    sx={{
                                      backgroundColor: isQualified
                                        ? "#e8f5e9"
                                        : "#ffebee",
                                      fontWeight: 600,
                                      color: "#333",
                                      fontSize: "13px",
                                      padding: "12px 16px",
                                      borderBottom: "2px solid #e0e0e0",
                                      borderRight:
                                        vendorIdx < vendors.length - 1
                                          ? "1px solid #e0e0e0"
                                          : "none",
                                      minWidth: 120,
                                    }}
                                  >
                                    Weighted Score
                                  </TableCell>
                                </React.Fragment>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allCriteria.map((criterion, criterionIdx) => (
                            <TableRow
                              key={criterionIdx}
                              sx={{
                                "&:hover": { backgroundColor: "#fafafa" },
                              }}
                            >
                              <TableCell
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "13px",
                                  color: "#333",
                                  padding: "16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  borderRight: "1px solid #e0e0e0",
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                {criterion.criterion_name}
                              </TableCell>
                              {vendors.map((vendor, vendorIdx) => {
                                const vendorCriterion =
                                  vendor.scores?.technical?.criteria?.find(
                                    (c) =>
                                      c.criterion_name ===
                                      criterion.criterion_name,
                                  );
                                // Use agent_score for the original agent score (before any modifications)
                                const agentScore =
                                  vendorCriterion?.agent_score ??
                                  vendorCriterion?.score ??
                                  0;
                                const maxScore =
                                  vendorCriterion?.max_score || 5;
                                const weight = vendorCriterion?.weight || 0;
                                const popoverKey = `${vendorIdx}_${criterionIdx}`;
                                const scoreKey = `${vendorIdx}_${criterionIdx}`;
                                const modifiedScore = modifiedScores[scoreKey];
                                const backendEffectiveScore =
                                  vendorCriterion?.effective_score;
                                const backendUserScore =
                                  vendorCriterion?.user_score;
                                // Prioritize: local modified score > backend user score > backend effective score > agent score
                                const effectiveScore =
                                  modifiedScore !== undefined
                                    ? modifiedScore
                                    : backendUserScore !== null &&
                                        backendUserScore !== undefined
                                      ? backendUserScore
                                      : backendEffectiveScore !== null &&
                                          backendEffectiveScore !== undefined
                                        ? backendEffectiveScore
                                        : agentScore;
                                const weightedScore =
                                  (effectiveScore / maxScore) * weight;
                                // Original weighted score from API (for info popover)
                                const originalWeightedScore =
                                  (agentScore / maxScore) * weight;
                                const isQualified =
                                  vendor.summary?.qualified !== false;
                                const isEditing = editingScoreKey === scoreKey;
                                // Check both local modifications and backend modifications
                                const hasLocalModifications = allCriteria.some(
                                  (criterion, criterionIdx) => {
                                    const vendorCriterion =
                                      vendor.scores?.technical?.criteria?.find(
                                        (c) =>
                                          c.criterion_name ===
                                          criterion.criterion_name,
                                      );
                                    const originalScore =
                                      vendorCriterion?.agent_score ??
                                      vendorCriterion?.score ??
                                      0;
                                    const scoreKey = `${vendorIdx}_${criterionIdx}`;
                                    const modifiedScore =
                                      modifiedScores[scoreKey];
                                    return (
                                      modifiedScore !== undefined &&
                                      modifiedScore !== originalScore
                                    );
                                  },
                                );
                                const hasBackendModifications =
                                  vendor.scores?.technical?.criteria?.some(
                                    (criterion) =>
                                      criterion.user_score !== null ||
                                      criterion.is_overridden,
                                  );
                                const hasModifiedScores =
                                  hasLocalModifications ||
                                  hasBackendModifications;

                                // Get the displayed modified score (prioritize local, then backend user score)
                                const displayedModifiedScore =
                                  modifiedScore !== undefined
                                    ? modifiedScore
                                    : backendUserScore;
                                const isScoreModified =
                                  displayedModifiedScore !== null &&
                                  displayedModifiedScore !== undefined &&
                                  displayedModifiedScore !== agentScore;

                                return (
                                  <React.Fragment key={vendorIdx}>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        backgroundColor: isQualified
                                          ? "#f1f8f4"
                                          : "#fef5f5",
                                        padding: "16px",
                                        borderBottom: "1px solid #e0e0e0",
                                        borderRight: "1px solid #e0e0e0",
                                        fontSize: "14px",
                                        color: "#333",
                                      }}
                                    >
                                      {isEditing ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          <TextField
                                            value={editingScoreValue}
                                            onChange={(e) =>
                                              setEditingScoreValue(
                                                e.target.value,
                                              )
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter")
                                                handleScoreSave();
                                              if (e.key === "Escape")
                                                handleScoreCancel();
                                            }}
                                            onBlur={handleScoreSave}
                                            autoFocus
                                            type="number"
                                            inputProps={{
                                              min: 0,
                                              max: 5,
                                              step: 0.1,
                                            }}
                                            sx={{
                                              width: "80px",
                                              "& .MuiInputBase-input": {
                                                padding: "4px 8px",
                                                fontSize: "14px",
                                                textAlign: "center",
                                              },
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          {agentScore.toFixed(1)}
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleScoreClick(
                                                vendorIdx,
                                                criterionIdx,
                                                agentScore,
                                              )
                                            }
                                            disabled={
                                              isSubmitting || hasApproved
                                            }
                                            sx={{
                                              padding: "4px",
                                              color: "#00a888",
                                              "&:hover": {
                                                backgroundColor: "#f0fbf9",
                                              },
                                            }}
                                          >
                                            <EditIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={(e) =>
                                              handleOpenScorePopover(
                                                popoverKey,
                                                e,
                                              )
                                            }
                                            sx={{
                                              padding: "4px",
                                              color: "#1976d2",
                                              "&:hover": {
                                                backgroundColor: "#e3f2fd",
                                              },
                                            }}
                                          >
                                            <InfoOutlinedIcon
                                              sx={{ fontSize: 18 }}
                                            />
                                          </IconButton>
                                        </Box>
                                      )}

                                      {/* Score Details Popover */}
                                      <Popover
                                        open={Boolean(
                                          scorePopoverAnchor[popoverKey],
                                        )}
                                        anchorEl={
                                          scorePopoverAnchor[popoverKey]
                                        }
                                        onClose={() =>
                                          handleCloseScorePopover(popoverKey)
                                        }
                                        anchorOrigin={{
                                          vertical: "bottom",
                                          horizontal: "center",
                                        }}
                                        transformOrigin={{
                                          vertical: "top",
                                          horizontal: "center",
                                        }}
                                      >
                                        <Paper
                                          sx={{
                                            maxWidth: 600,
                                            maxHeight: 500,
                                            overflow: "auto",
                                          }}
                                        >
                                          <Box sx={{ p: 3 }}>
                                            <Typography
                                              sx={{
                                                fontSize: "16px",
                                                fontWeight: 600,
                                                color: "#004141",
                                                mb: 2,
                                                pb: 1,
                                                borderBottom:
                                                  "2px solid #00a888",
                                              }}
                                            >
                                              {criterion.criterion_name} -{" "}
                                              {vendor.vendor_name}
                                            </Typography>

                                            {/* Score Info */}
                                            <Box
                                              sx={{
                                                mb: 2,
                                                p: 2,
                                                backgroundColor: "#f5f5f5",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  mb: 1,
                                                }}
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "#666",
                                                  }}
                                                >
                                                  Score:
                                                </Typography>
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "#333",
                                                  }}
                                                >
                                                  {agentScore.toFixed(1)} /{" "}
                                                  {maxScore.toFixed(1)}
                                                </Typography>
                                              </Box>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  mb: 1,
                                                }}
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "#666",
                                                  }}
                                                >
                                                  Weight:
                                                </Typography>
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "#333",
                                                  }}
                                                >
                                                  {(
                                                    vendorCriterion?.weight || 0
                                                  ).toFixed(0)}
                                                  %
                                                </Typography>
                                              </Box>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                }}
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "#666",
                                                  }}
                                                >
                                                  Weighted Score:
                                                </Typography>
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "#00a888",
                                                  }}
                                                >
                                                  {originalWeightedScore.toFixed(
                                                    1,
                                                  )}
                                                </Typography>
                                              </Box>
                                            </Box>

                                            {/* Justification */}
                                            {vendorCriterion?.justification && (
                                              <Box sx={{ mb: 2 }}>
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "#004141",
                                                    mb: 1,
                                                  }}
                                                >
                                                  Justification
                                                </Typography>
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    color: "#2a312b",
                                                    lineHeight: 1.6,
                                                  }}
                                                >
                                                  {
                                                    vendorCriterion.justification
                                                  }
                                                </Typography>
                                              </Box>
                                            )}

                                            {/* Evidence */}
                                            {vendorCriterion?.evidence && (
                                              <Box sx={{ mb: 2 }}>
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "#004141",
                                                    mb: 1,
                                                  }}
                                                >
                                                  Evidence
                                                </Typography>
                                                <Typography
                                                  sx={{
                                                    fontSize: "12px",
                                                    color: "#2a312b",
                                                    lineHeight: 1.6,
                                                    maxHeight: "150px",
                                                    overflow: "auto",
                                                    padding: "8px",
                                                    backgroundColor: "#fafafa",
                                                    borderRadius: "4px",
                                                    whiteSpace: "pre-wrap",
                                                  }}
                                                >
                                                  {vendorCriterion.evidence}
                                                </Typography>
                                              </Box>
                                            )}

                                            {/* Sub-Metrics */}
                                            {vendorCriterion?.sub_metrics &&
                                              vendorCriterion.sub_metrics
                                                .length > 0 && (
                                                <Box>
                                                  <Typography
                                                    sx={{
                                                      fontSize: "13px",
                                                      fontWeight: 600,
                                                      color: "#004141",
                                                      mb: 1,
                                                    }}
                                                  >
                                                    Sub-Metrics
                                                  </Typography>
                                                  {vendorCriterion.sub_metrics.map(
                                                    (subMetric, subIdx) => (
                                                      <Box
                                                        key={subIdx}
                                                        sx={{
                                                          mb: 2,
                                                          p: 2,
                                                          backgroundColor:
                                                            "#f9f9f9",
                                                          borderRadius: "4px",
                                                          border:
                                                            "1px solid #e0e0e0",
                                                        }}
                                                      >
                                                        <Box
                                                          sx={{
                                                            display: "flex",
                                                            justifyContent:
                                                              "space-between",
                                                            mb: 1,
                                                          }}
                                                        >
                                                          <Typography
                                                            sx={{
                                                              fontSize: "12px",
                                                              fontWeight: 600,
                                                              color: "#004141",
                                                            }}
                                                          >
                                                            {
                                                              subMetric.sub_metric_name
                                                            }
                                                          </Typography>
                                                          <Chip
                                                            label={(
                                                              subMetric.score ??
                                                              0
                                                            ).toFixed(1)}
                                                            size="small"
                                                            sx={{
                                                              backgroundColor:
                                                                "#00a888",
                                                              color: "#fff",
                                                              fontWeight: 600,
                                                              height: "20px",
                                                              fontSize: "11px",
                                                            }}
                                                          />
                                                        </Box>
                                                        <Typography
                                                          sx={{
                                                            fontSize: "11px",
                                                            color: "#666",
                                                            mb: 0.5,
                                                          }}
                                                        >
                                                          {
                                                            subMetric.justification
                                                          }
                                                        </Typography>
                                                      </Box>
                                                    ),
                                                  )}
                                                </Box>
                                              )}
                                          </Box>
                                        </Paper>
                                      </Popover>
                                    </TableCell>
                                    {hasModifiedScores && (
                                      <TableCell
                                        align="center"
                                        sx={{
                                          backgroundColor: "#FFFACD",
                                          padding: "16px",
                                          borderBottom: "1px solid #e0e0e0",
                                          borderRight: "1px solid #e0e0e0",
                                          fontSize: "14px",
                                          color: "#000",
                                          fontWeight: isScoreModified
                                            ? 600
                                            : 400,
                                        }}
                                      >
                                        {isScoreModified
                                          ? displayedModifiedScore.toFixed(1)
                                          : "-"}
                                      </TableCell>
                                    )}
                                    <TableCell
                                      align="center"
                                      sx={{
                                        backgroundColor: isQualified
                                          ? "#f1f8f4"
                                          : "#fef5f5",
                                        padding: "16px",
                                        borderBottom: "1px solid #e0e0e0",
                                        borderRight:
                                          vendorIdx < vendors.length - 1
                                            ? "1px solid #e0e0e0"
                                            : "none",
                                        fontSize: "14px",
                                        color: "#333",
                                      }}
                                    >
                                      {weightedScore.toFixed(1)}
                                    </TableCell>
                                  </React.Fragment>
                                );
                              })}
                            </TableRow>
                          ))}
                          {/* Total Row */}
                          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "14px",
                                color: "#333",
                                padding: "16px",
                                borderBottom: "2px solid #e0e0e0",
                                borderRight: "1px solid #e0e0e0",
                              }}
                            >
                              Total
                            </TableCell>
                            {vendors.map((vendor, vendorIdx) => {
                              const isQualified =
                                vendor.summary?.qualified !== false;
                              // Check both local modifications and backend modifications
                              const hasLocalModifications = allCriteria.some(
                                (criterion, criterionIdx) => {
                                  const vendorCriterion =
                                    vendor.scores?.technical?.criteria?.find(
                                      (c) =>
                                        c.criterion_name ===
                                        criterion.criterion_name,
                                    );
                                  const originalScore =
                                    vendorCriterion?.agent_score ??
                                    vendorCriterion?.score ??
                                    0;
                                  const scoreKey = `${vendorIdx}_${criterionIdx}`;
                                  const modifiedScore =
                                    modifiedScores[scoreKey];
                                  return (
                                    modifiedScore !== undefined &&
                                    modifiedScore !== originalScore
                                  );
                                },
                              );
                              const hasBackendModifications =
                                vendor.scores?.technical?.criteria?.some(
                                  (criterion) =>
                                    criterion.user_score !== null ||
                                    criterion.is_overridden,
                                );
                              const hasModifiedScores =
                                hasLocalModifications ||
                                hasBackendModifications;

                              // Calculate total weighted score (use modified scores if available)
                              let totalWeightedScore = 0;
                              allCriteria.forEach((criterion, criterionIdx) => {
                                const vendorCriterion =
                                  vendor.scores?.technical?.criteria?.find(
                                    (c) =>
                                      c.criterion_name ===
                                      criterion.criterion_name,
                                  );
                                const agentScore =
                                  vendorCriterion?.agent_score ??
                                  vendorCriterion?.score ??
                                  0;
                                const maxScore =
                                  vendorCriterion?.max_score || 5;
                                const weight = vendorCriterion?.weight || 0;
                                const scoreKey = `${vendorIdx}_${criterionIdx}`;
                                const modifiedScore = modifiedScores[scoreKey];
                                const backendEffectiveScore =
                                  vendorCriterion?.effective_score;
                                const backendUserScore =
                                  vendorCriterion?.user_score;
                                // Prioritize: local modified score > backend user score > backend effective score > agent score
                                const effectiveScore =
                                  modifiedScore !== undefined
                                    ? modifiedScore
                                    : backendUserScore !== null &&
                                        backendUserScore !== undefined
                                      ? backendUserScore
                                      : backendEffectiveScore !== null &&
                                          backendEffectiveScore !== undefined
                                        ? backendEffectiveScore
                                        : agentScore;
                                totalWeightedScore +=
                                  (effectiveScore / maxScore) * weight;
                              });

                              return (
                                <React.Fragment key={vendorIdx}>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      backgroundColor: isQualified
                                        ? "#e8f5e9"
                                        : "#ffebee",
                                      padding: "16px",
                                      borderBottom: "2px solid #e0e0e0",
                                      borderRight: "1px solid #e0e0e0",
                                      fontSize: "16px",
                                      fontWeight: 700,
                                      color: "#333",
                                    }}
                                  >
                                    -
                                  </TableCell>
                                  {hasModifiedScores && (
                                    <TableCell
                                      align="center"
                                      sx={{
                                        backgroundColor: "#FFFACD",
                                        padding: "16px",
                                        borderBottom: "2px solid #e0e0e0",
                                        borderRight: "1px solid #e0e0e0",
                                        fontSize: "16px",
                                        fontWeight: 700,
                                        color: "#000",
                                      }}
                                    >
                                      -
                                    </TableCell>
                                  )}
                                  <TableCell
                                    align="center"
                                    sx={{
                                      backgroundColor: isQualified
                                        ? "#e8f5e9"
                                        : "#ffebee",
                                      padding: "16px",
                                      borderBottom: "2px solid #e0e0e0",
                                      borderRight:
                                        vendorIdx < vendors.length - 1
                                          ? "1px solid #e0e0e0"
                                          : "none",
                                      fontSize: "16px",
                                      fontWeight: 700,
                                      color: "#333",
                                    }}
                                  >
                                    {totalWeightedScore.toFixed(1)}
                                  </TableCell>
                                </React.Fragment>
                              );
                            })}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  {/* Approve Button */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleApproveTechnicalScores}
                      disabled={isSubmitting || hasApproved}
                      sx={{
                        backgroundColor: "#5fcfac",
                        color: "#fff",
                        fontWeight: 600,
                        textTransform: "none",
                        padding: "10px 24px",
                        "&:hover": {
                          backgroundColor: "#4db89a",
                        },
                        "&:disabled": {
                          backgroundColor: "#ccc",
                        },
                      }}
                    >
                      {isSubmitting
                        ? Object.keys(modifiedScores).length > 0
                          ? "Submitting..."
                          : "Approving..."
                        : Object.keys(modifiedScores).length > 0
                          ? "Submit"
                          : "Approve"}
                    </Button>
                  </Box>
                </>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    textAlign: "center",
                    border: "1px solid #b2e5de",
                    borderRadius: "8px",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>
                    No vendors evaluated
                  </Typography>
                </Paper>
              )}
            </>
          );
        })()}

      {/* Summary Response */}
      {status === "summary" && message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              "& h1, & h2, & h3": {
                fontSize: "16px",
                fontWeight: 600,
                color: "#004141",
                mb: 1.5,
                mt: 2,
              },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
              "& code": {
                backgroundColor: "#e0f2f1",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "13px",
                fontFamily: "monospace",
              },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Excel Export Ready */}
      {status === "export_ready" && downloadUrl && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <CheckCircleIcon sx={{ color: "#00a888", fontSize: 32 }} />
            <Typography
              component="span"
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#004141",
                my: 0,
              }}
            >
              Excel Export Generated
            </Typography>
          </Box>

          {message &&
            (() => {
              // Filter out the download URL section from the message
              // Keep "What You Can Do:" section but remove the blob storage URL
              let filteredMessage = message;

              // Remove "(link expires in ...)" italic annotations from the message
              filteredMessage = filteredMessage
                .replace(/\*\(link expires[^)]*\)\*/gi, "")
                .replace(/\(link expires[^)]*\)/gi, "");

              // Remove the "📥 Download Excel Report" section up to "What You Can Do:"
              const downloadSectionIndex = filteredMessage.indexOf(
                "📥 **Download Excel Report**",
              );
              const whatYouCanDoIndex = filteredMessage.indexOf(
                "**What You Can Do:**",
              );

              if (downloadSectionIndex !== -1 && whatYouCanDoIndex !== -1) {
                // Keep everything before download section and everything from "What You Can Do:"
                const beforeDownload = filteredMessage
                  .substring(0, downloadSectionIndex)
                  .trim();
                const afterWhatYouCanDo = filteredMessage
                  .substring(whatYouCanDoIndex)
                  .trim();
                filteredMessage = beforeDownload + "\n\n" + afterWhatYouCanDo;
              } else if (downloadSectionIndex !== -1) {
                // If only download section exists, remove it
                filteredMessage = filteredMessage
                  .substring(0, downloadSectionIndex)
                  .trim();
              }
              return filteredMessage ? (
                <Box
                  sx={{
                    mb: 3,
                    fontSize: "14px",
                    color: "#2a312b",
                    "& h1, & h2, & h3": {
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#004141",
                      mb: 1.5,
                      mt: 2,
                    },
                    "& p": { margin: "8px 0" },
                    "& strong": { fontWeight: 600, color: "#004141" },
                    "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
                    "& li": { marginBottom: "4px" },
                    "& code": {
                      backgroundColor: "#e0f2f1",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontSize: "13px",
                      fontFamily: "monospace",
                    },
                  }}
                >
                  <MarkdownRenderer content={filteredMessage} />
                </Box>
              ) : null;
            })()}

          {/* Evaluation Summary */}
          {parsedOutput && (
            <>
              {/* Vendor Rankings */}
              {parsedOutput.vendor_scores &&
                parsedOutput.vendor_scores.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#004141",
                        mb: 2,
                      }}
                    >
                      Vendor Rankings
                    </Typography>
                    <TableContainer
                      component={Paper}
                      sx={{
                        border: "1px solid #b2e5de",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f0fbf9" }}>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#004141",
                                fontSize: "13px",
                              }}
                            >
                              Rank
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#004141",
                                fontSize: "13px",
                              }}
                            >
                              Vendor Name
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                color: "#004141",
                                fontSize: "13px",
                              }}
                            >
                              Final Score
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                color: "#004141",
                                fontSize: "13px",
                              }}
                            >
                              Threshold
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                color: "#004141",
                                fontSize: "13px",
                              }}
                            >
                              Status
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {parsedOutput.vendor_scores.map(
                            (vendor: any, index: number) => {
                              const isQualified =
                                vendor.summary?.qualified ??
                                vendor.technical_score_percent >=
                                  vendor.technical_threshold;
                              return (
                                <TableRow
                                  key={index}
                                  sx={{
                                    backgroundColor: isQualified
                                      ? "#f1f8f4"
                                      : "#fef5f5",
                                    "&:hover": {
                                      backgroundColor: isQualified
                                        ? "#e8f5e9"
                                        : "#ffebee",
                                    },
                                  }}
                                >
                                  <TableCell sx={{ fontSize: "13px" }}>
                                    <Chip
                                      label={`#${vendor.rank}`}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#004141",
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#333",
                                    }}
                                  >
                                    {vendor.vendor_name}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ fontSize: "13px" }}
                                  >
                                    <Chip
                                      label={(vendor.final_score ?? 0).toFixed(
                                        1,
                                      )}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#00a888",
                                        color: "#fff",
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ fontSize: "13px", color: "#666" }}
                                  >
                                    {vendor.technical_threshold}%
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={
                                        isQualified
                                          ? "Qualified"
                                          : "Not Qualified"
                                      }
                                      size="small"
                                      sx={{
                                        backgroundColor: isQualified
                                          ? "#4caf50"
                                          : "#f44336",
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: "11px",
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            },
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
            </>
          )}

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => {
                // Record download action - fire and forget (non-blocking)
                dispatch(
                  recordAgentAction({
                    agentId: numericAgentId,
                    actionType: "download",
                    content: JSON.stringify({
                      fileName: `RFQ_Evaluation_${workflowId}.xlsx`,
                      format: "excel",
                      sessionId,
                      workflowId: workflowId || null,
                    }),
                  })
                );

                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = `RFQ_Evaluation_${workflowId}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              sx={{
                backgroundColor: "#00a888",
                "&:hover": { backgroundColor: "#008f75" },
                textTransform: "none",
                fontWeight: 600,
                px: 4,
              }}
            >
              Download Excel Report
            </Button>
          </Box>
        </Paper>
      )}

      {/* Scoring in Progress */}
      {status === "scoring" && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <AssessmentIcon sx={{ color: "#00a888", fontSize: 32 }} />
            <Typography
              sx={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
            >
              {message || "Scoring vendors..."}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "#d0f2ed",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#00a888",
              },
            }}
          />
          <Typography
            sx={{ fontSize: "13px", color: "#666", mt: 1, textAlign: "center" }}
          >
            {progress}%
          </Typography>
        </Paper>
      )}

      {/* Results Display */}
      {status === "completed" && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: "#f0fbf9",
              border: "1px solid #b2e5de",
              borderRadius: "8px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CheckCircleIcon sx={{ color: "#00a888", fontSize: 32 }} />
              <Typography
                sx={{ fontSize: "18px", fontWeight: 600, color: "#004141" }}
              >
                Evaluation Completed
              </Typography>
            </Box>
            {message && (
              <Box
                sx={{
                  mb: 2,
                  fontSize: "14px",
                  color: "#2a312b",
                  "& p": { margin: "8px 0" },
                  "& strong": { fontWeight: 600, color: "#004141" },
                }}
              >
                <MarkdownRenderer content={message} />
              </Box>
            )}
            {/* Evaluation Summary */}
            {(evaluationMethod || totalCriteria > 0 || recommendation) && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#fff",
                  borderRadius: "6px",
                  border: "1px solid #b2e5de",
                }}
              >
                {evaluationMethod && (
                  <Typography sx={{ fontSize: "13px", color: "#666", mb: 1 }}>
                    <strong>Evaluation Method:</strong>{" "}
                    {evaluationMethod === "techno_commercial"
                      ? "Techno-Commercial"
                      : evaluationMethod === "technical_threshold"
                        ? "Technical Threshold"
                        : evaluationMethod}
                  </Typography>
                )}
                {totalCriteria > 0 && (
                  <Typography sx={{ fontSize: "13px", color: "#666", mb: 1 }}>
                    <strong>Criteria:</strong> {technicalCount} Technical,{" "}
                    {commercialCount} Commercial (Total: {totalCriteria})
                  </Typography>
                )}
                {recommendation && (
                  <Typography sx={{ fontSize: "13px", color: "#666", mb: 1 }}>
                    <strong>Recommendation:</strong> {recommendation}
                  </Typography>
                )}
                {evaluationDate && (
                  <Typography sx={{ fontSize: "13px", color: "#666" }}>
                    <strong>Date:</strong>{" "}
                    {new Date(evaluationDate).toLocaleString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                )}
              </Box>
            )}

            {/* Winner Highlight */}
            {recommendedVendor && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#e8f5e9",
                  borderRadius: "6px",
                  border: "1px solid #2e7d32",
                }}
              >
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 500, color: "#2e7d32" }}
                >
                  🏆 Recommended: {recommendedVendor}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Vendor Rankings Table */}
          {vendors.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                border: "1px solid #b2e5de",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f0fbf9",
                  borderBottom: "1px solid #b2e5de",
                }}
              >
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 600, color: "#004141" }}
                >
                  Vendor Rankings
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#fafafa" }}>
                      <TableCell sx={{ fontWeight: 600, color: "#004141" }}>
                        Rank
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#004141" }}>
                        Vendor
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#004141" }}>
                        Score
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#004141" }}>
                        Percentage
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#004141" }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#004141" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendors
                      .sort((a, b) => a.ranking - b.ranking)
                      .map((vendor) => {
                        const statusStyle = getStatusStyle(vendor.status);
                        return (
                          <React.Fragment key={vendor.vendor_id}>
                            <TableRow
                              sx={{
                                "&:hover": { backgroundColor: "#f8f9fa" },
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                toggleVendorExpansion(vendor.vendor_id)
                              }
                            >
                              <TableCell>
                                <Chip
                                  label={`L${vendor.ranking}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#004141",
                                    color: "#fff",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {vendor.vendor_name}
                              </TableCell>
                              <TableCell>
                                {(vendor.overall_score ?? 0).toFixed(1)}
                              </TableCell>
                              <TableCell>
                                {(vendor.overall_percentage ?? 0).toFixed(1)}%
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={vendor.status}
                                  size="small"
                                  sx={{
                                    backgroundColor: statusStyle.bg,
                                    color: statusStyle.color,
                                    fontWeight: 500,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleVendorExpansion(vendor.vendor_id);
                                  }}
                                >
                                  {expandedVendors[vendor.vendor_id] ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            {expandedVendors[vendor.vendor_id] && (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  sx={{ p: 3, backgroundColor: "#fafafa" }}
                                >
                                  <Box>
                                    {/* Recommendation */}
                                    {vendor.recommendation && (
                                      <Box sx={{ mb: 3 }}>
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: "#004141",
                                            mb: 1,
                                          }}
                                        >
                                          Recommendation
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            color: "#2a312b",
                                          }}
                                        >
                                          {vendor.recommendation}
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Strengths */}
                                    {vendor.strengths &&
                                      vendor.strengths.length > 0 && (
                                        <Box sx={{ mb: 3 }}>
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              color: "#2e7d32",
                                              mb: 1,
                                            }}
                                          >
                                            ✓ Strengths
                                          </Typography>
                                          <ul
                                            style={{
                                              margin: 0,
                                              paddingLeft: "20px",
                                            }}
                                          >
                                            {vendor.strengths.map(
                                              (strength, idx) => (
                                                <li
                                                  key={idx}
                                                  style={{
                                                    fontSize: "14px",
                                                    color: "#2a312b",
                                                  }}
                                                >
                                                  {strength}
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        </Box>
                                      )}

                                    {/* Weaknesses */}
                                    {vendor.weaknesses &&
                                      vendor.weaknesses.length > 0 && (
                                        <Box sx={{ mb: 3 }}>
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              color: "#c62828",
                                              mb: 1,
                                            }}
                                          >
                                            ✗ Weaknesses
                                          </Typography>
                                          <ul
                                            style={{
                                              margin: 0,
                                              paddingLeft: "20px",
                                            }}
                                          >
                                            {vendor.weaknesses.map(
                                              (weakness, idx) => (
                                                <li
                                                  key={idx}
                                                  style={{
                                                    fontSize: "14px",
                                                    color: "#2a312b",
                                                  }}
                                                >
                                                  {weakness}
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        </Box>
                                      )}

                                    {/* Detailed Scores */}
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: "14px",
                                          fontWeight: 600,
                                          color: "#004141",
                                          mb: 2,
                                        }}
                                      >
                                        Detailed Scoring
                                      </Typography>
                                      {vendor.scores_by_criterion.map(
                                        (score, scoreIdx) => {
                                          const ratingStyle = getRatingStyle(
                                            score.rating,
                                          );
                                          const isScoreOpen =
                                            expandedVendors[
                                              `${vendor.vendor_id}_score_${scoreIdx}`
                                            ] || false;

                                          return (
                                            <Box
                                              key={score.criterion_id}
                                              sx={{
                                                mb: 2,
                                                border: "1px solid #b2e5de",
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                              }}
                                            >
                                              {/* Accordion Header */}
                                              <Box
                                                onClick={() =>
                                                  toggleVendorExpansion(
                                                    `${vendor.vendor_id}_score_${scoreIdx}`,
                                                  )
                                                }
                                                sx={{
                                                  background: "#f0fbf9",
                                                  padding: "16px 20px",
                                                  cursor: "pointer",
                                                  transition: "background 0.2s",
                                                  "&:hover": {
                                                    background: "#e8f8f6",
                                                  },
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "flex-start",
                                                  gap: 2,
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 1,
                                                    flex: 1,
                                                    minWidth: 0,
                                                  }}
                                                >
                                                  {isScoreOpen ? (
                                                    <ExpandLessIcon
                                                      sx={{
                                                        color: "#006d77",
                                                        fontSize: "24px",
                                                        flexShrink: 0,
                                                        mt: 0.2,
                                                      }}
                                                    />
                                                  ) : (
                                                    <ExpandMoreIcon
                                                      sx={{
                                                        color: "#006d77",
                                                        fontSize: "24px",
                                                        flexShrink: 0,
                                                        mt: 0.2,
                                                      }}
                                                    />
                                                  )}
                                                  <Box
                                                    component="span"
                                                    sx={{
                                                      fontSize: "14px",
                                                      fontWeight: 500,
                                                      color: "#004141",
                                                      lineHeight: 1.4,
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {score.criterion_name.replace(
                                                      /\s*:\s*$/,
                                                      "",
                                                    )}
                                                  </Box>
                                                </Box>
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    gap: 1,
                                                    alignItems: "center",
                                                    flexShrink: 0,
                                                  }}
                                                >
                                                  <Chip
                                                    label={score.rating}
                                                    size="small"
                                                    sx={{
                                                      backgroundColor:
                                                        ratingStyle.bg,
                                                      color: ratingStyle.color,
                                                      fontWeight: 500,
                                                      fontSize: "11px",
                                                      height: "22px",
                                                    }}
                                                  />
                                                  <Box
                                                    component="span"
                                                    sx={{
                                                      fontSize: "13px",
                                                      fontWeight: 500,
                                                      color: "#004141",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {score.score}/
                                                    {score.max_score} (
                                                    {(
                                                      score.weighted_score ?? 0
                                                    ).toFixed(1)}
                                                    )
                                                  </Box>
                                                </Box>
                                              </Box>

                                              {/* Accordion Content */}
                                              {isScoreOpen && (
                                                <Box
                                                  sx={{
                                                    padding: "20px",
                                                    background: "#ffffff",
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: "13px",
                                                      color: "#2a312b",
                                                      mb: 2,
                                                      lineHeight: 1.6,
                                                    }}
                                                  >
                                                    {score.justification}
                                                  </Typography>
                                                  {score.evidence_references
                                                    .length > 0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                      <Typography
                                                        sx={{
                                                          fontSize: "13px",
                                                          fontWeight: 600,
                                                          color: "#004141",
                                                          mb: 1,
                                                        }}
                                                      >
                                                        Evidence:
                                                      </Typography>
                                                      {score.evidence_references.map(
                                                        (ref, idx) => (
                                                          <Box
                                                            key={idx}
                                                            sx={{
                                                              mb: 1,
                                                              p: 1.5,
                                                              backgroundColor:
                                                                "#f8f9fa",
                                                              borderRadius:
                                                                "4px",
                                                              borderLeft:
                                                                "3px solid #00a888",
                                                              "& p": {
                                                                margin: "8px 0",
                                                                fontSize:
                                                                  "12px",
                                                              },
                                                              "& h1, & h2, & h3":
                                                                {
                                                                  fontSize:
                                                                    "13px",
                                                                  fontWeight: 600,
                                                                  color:
                                                                    "#004141",
                                                                  mt: 2,
                                                                  mb: 1,
                                                                },
                                                              "& ul, & ol": {
                                                                marginLeft:
                                                                  "20px",
                                                                marginTop:
                                                                  "8px",
                                                                fontSize:
                                                                  "12px",
                                                              },
                                                              "& li": {
                                                                marginBottom:
                                                                  "4px",
                                                              },
                                                              "& strong": {
                                                                fontWeight: 600,
                                                                color:
                                                                  "#004141",
                                                              },
                                                              "& code": {
                                                                backgroundColor:
                                                                  "#e0e0e0",
                                                                padding:
                                                                  "2px 4px",
                                                                borderRadius:
                                                                  "3px",
                                                                fontSize:
                                                                  "11px",
                                                              },
                                                            }}
                                                          >
                                                            <MarkdownRenderer
                                                              content={ref}
                                                            />
                                                          </Box>
                                                        ),
                                                      )}
                                                    </Box>
                                                  )}
                                                </Box>
                                              )}
                                            </Box>
                                          );
                                        },
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Download Buttons */}
          {downloadUrl && (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadReport("excel")}
                disabled={isDownloading}
                sx={{
                  backgroundColor: "#00a888",
                  "&:hover": { backgroundColor: "#008f75" },
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Download Excel Report
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Error State */}
      {status === "error" && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#ffebee",
            border: "1px solid #c62828",
            borderRadius: "8px",
          }}
        >
          <Typography
            sx={{ fontSize: "16px", fontWeight: 500, color: "#c62828", mb: 1 }}
          >
            Evaluation Error
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#2a312b" }}>
            {message}
          </Typography>
        </Paper>
      )}

      {/* Fallback - show message even if status not recognized */}
      {!status && message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              "& h1, & h2, & h3": {
                fontSize: "16px",
                fontWeight: 600,
                color: "#004141",
                mb: 1.5,
                mt: 2,
              },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
              "& code": {
                backgroundColor: "#e0f2f1",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "13px",
                fontFamily: "monospace",
              },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Fallback - initializing */}
      {!status && !message && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography
            sx={{ fontSize: "14px", color: "#666", fontStyle: "italic" }}
          >
            Initializing RFQ Response Evaluator...
          </Typography>
        </Box>
      )}

      {/* Edit Criterion Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f0fbf9",
            color: "#004141",
            fontWeight: 600,
          }}
        >
          Edit Criterion
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editingIndex !== null && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
            >
              <TextField
                label="Criterion Name"
                value={editedCriterionName}
                onChange={(e) => setEditedCriterionName(e.target.value)}
                fullWidth
                disabled={isSubmitting || hasApproved}
                placeholder="e.g., Technical Expertise"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#00a888",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#00a888",
                    },
                  },
                }}
              />

              <TextField
                label="Percentage Weightage"
                value={editedWeightage}
                onChange={(e) => setEditedWeightage(e.target.value)}
                fullWidth
                disabled={isSubmitting || hasApproved}
                type="number"
                placeholder="e.g., 21"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#00a888",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#00a888",
                    },
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCancelEdit}
            sx={{
              color: "#666",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={isSubmitting || hasApproved}
            sx={{
              backgroundColor: "#00a888",
              "&:hover": { backgroundColor: "#008f75" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Technical Threshold Edit Dialog */}
      <Dialog
        open={thresholdDialogOpen}
        onClose={handleThresholdCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f0fbf9",
            color: "#004141",
            fontWeight: 600,
          }}
        >
          Edit{" "}
          {parsedOutput?.eval_method
            ? parsedOutput.eval_method
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l: string) => l.toUpperCase())
            : "Technical Threshold"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              label={
                parsedOutput?.eval_method
                  ? parsedOutput.eval_method
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l: string) => l.toUpperCase())
                  : "Technical Threshold"
              }
              value={editedTechnicalThreshold ?? ""}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value) && value >= 0 && value <= 100) {
                  setEditedTechnicalThreshold(value);
                } else if (e.target.value === "") {
                  setEditedTechnicalThreshold(null);
                }
              }}
              fullWidth
              disabled={isSubmitting || hasApproved}
              type="number"
              placeholder="Enter value (0-100)"
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText="Enter a value between 0 and 100"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#00a888",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#00a888",
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleThresholdCancel}
            sx={{
              color: "#666",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleThresholdSave}
            variant="contained"
            disabled={
              isSubmitting ||
              hasApproved ||
              editedTechnicalThreshold === null ||
              editedTechnicalThreshold < 0 ||
              editedTechnicalThreshold > 100
            }
            sx={{
              backgroundColor: "#00a888",
              "&:hover": { backgroundColor: "#008f75" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
